import _ from 'lodash';
import { lsApiService, lsErrorService } from 'ls-vue-common';
import { lsModuleHelpers } from '../helpers';

import {
  CONVERSATION__RESET,
  CONVERSATION__MUTATE_LOADING_STATUS,
  CONVERSATION__MUTATE_MESSAGES,
  CONVERSATION__MUTATE_CONVERSATIONS_BY_CONTRACTOR_ID,
  CONVERSATION__MUTATE_APPEND_MESSAGE,
  CONVERSATION__MUTATE_SET_CONVERSATIONS,
  CONVERSATION__MUTATE_SET_CONTACTS,
  CONVERSATION__MUTATE_SET_CONVERSATION,
  CONVERSATION__GET_CONVERSATIONS,
  CONVERSATION__GET_CONVERSATION,
  CONVERSATION__START_CONVERSATION,
  CONVERSATION__SEND_MESSAGE,
  CONVERSATION__REJECT_DAY_SWITCH,
  CONVERSATION__UNDUPED_CONTACTS,
} from '@/store/types';

const createInitialState = () => {
  return {
    conversations: [],
    conversationsByContractorId: {},
    allContacts: [],
    currentConversation: {
      contractor: null,
      conversation_id: null,
      customer: null,
      is_active: null,
      messages: [],
      unread: null,
      updated_at: null,
    },
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
      isUpdatingMessageID: null,
    },
  };
};

export const mutations = {
  [CONVERSATION__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },
  [CONVERSATION__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
  [CONVERSATION__MUTATE_MESSAGES](state, payload) {
    state.currentConversation.messages = [...payload];
  },
  [CONVERSATION__MUTATE_CONVERSATIONS_BY_CONTRACTOR_ID](state, payload) {
    const contractors = {};
    _.forEach(payload, (item) => {
      _.set(contractors, item.contractor.contractor_id, true);
    });
    state.conversationsByContractorId = { ...contractors };
  },
  [CONVERSATION__MUTATE_APPEND_MESSAGE](state, payload) {
    state.currentConversation.messages = [...state.currentConversation.messages, payload];
  },
  [CONVERSATION__MUTATE_SET_CONVERSATIONS](state, payload) {
    state.conversations = [...payload];
  },
  [CONVERSATION__MUTATE_SET_CONTACTS](state, payload) {
    state.allContacts = [...payload];
  },
  [CONVERSATION__MUTATE_SET_CONVERSATION](state, payload) {
    state.currentConversation = { ...payload };
  },
};

export const actions = {
  [CONVERSATION__GET_CONVERSATIONS]({ commit, rootState }) {
    commit(CONVERSATION__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    const { user_id } = rootState.auth.user;

    const allConversations = lsApiService.request({
      method: 'GET',
      route: `/conversations?limit=20&page=1`,
    });

    const allContacts = lsApiService.request({
      method: 'GET',
      route: `/users/${user_id}/contacts`,
    });

    return Promise.all([allConversations, allContacts])
      .then((data) => {
        commit(CONVERSATION__MUTATE_SET_CONVERSATIONS, data[0].data);
        commit(CONVERSATION__MUTATE_CONVERSATIONS_BY_CONTRACTOR_ID, data[0].data);
        commit(CONVERSATION__MUTATE_SET_CONTACTS, data[1].data);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(CONVERSATION__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },

  [CONVERSATION__GET_CONVERSATION]({ commit }, { conversation_id }) {
    commit(CONVERSATION__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });
    return lsApiService
      .request({
        method: 'GET',
        route: `/conversations/${conversation_id}`,
      })
      .then(({ data }) => {
        commit(CONVERSATION__MUTATE_SET_CONVERSATION, data);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(CONVERSATION__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },

  [CONVERSATION__START_CONVERSATION]({ commit }, { contractorUserId }) {
    commit(CONVERSATION__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: '/conversations',
        payload: {
          user: { user_id: contractorUserId },
        },
      })
      .then(({ data }) => {
        commit(CONVERSATION__MUTATE_SET_CONVERSATION, data);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(CONVERSATION__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },

  [CONVERSATION__SEND_MESSAGE]({ commit, state }, { message }) {
    commit(CONVERSATION__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });
    const payload = { message };

    return lsApiService
      .request({
        method: 'POST',
        route: `/conversations/${state.currentConversation.conversation_id}/messages`,
        payload,
      })
      .then(({ data }) => {
        commit(CONVERSATION__MUTATE_APPEND_MESSAGE, data);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(CONVERSATION__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [CONVERSATION__REJECT_DAY_SWITCH](
    { commit, state, rootState },
    { message_id, schedule_event_day_switch_id, scheduleEvent }
  ) {
    const { customer_id } = rootState.auth.user;
    const messages = _.cloneDeep(state.currentConversation.messages);

    commit(CONVERSATION__MUTATE_LOADING_STATUS, {
      isUpdatingMessageID: message_id,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${
          scheduleEvent.schedule.property.property_id
        }/schedules/${scheduleEvent.schedule.schedule_id}/scheduleevents/${
          scheduleEvent.scheduleevent_id
        }/dayswitches/${schedule_event_day_switch_id}/reject`,
      })
      .then(({ data }) => {
        const idx = _.findIndex(messages, { message_id });
        _.set(messages[idx], 'link.entity.rejected_at', data.rejected_at);
        commit(CONVERSATION__MUTATE_MESSAGES, messages);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(CONVERSATION__MUTATE_LOADING_STATUS, {
          isUpdatingMessageID: null,
        });
      });
  },
};

export const getters = {
  [CONVERSATION__UNDUPED_CONTACTS](state) {
    return _.filter(state.allContacts, (contact) => {
      const contractor_id = _.get(contact, 'contractor.contractor_id');
      return !_.get(state.conversationsByContractorId, contractor_id, false);
    });
  },
};

export const conversation = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
